import React from 'react';
import { getPasswordStrength, type PasswordStrength } from '../../lib/auth/passwordSecurity';
import { Shield } from 'lucide-react';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

export function PasswordStrengthMeter({ password, className }: PasswordStrengthMeterProps) {
  const strength = getPasswordStrength(password);

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Shield className={`mr-2 h-4 w-4 ${getIconColor(strength)}`} />
          <span className="text-sm font-medium text-gray-700">{strength.label}</span>
        </div>
        <span className="text-sm text-gray-500">{Math.round(strength.percentage)}%</span>
      </div>

      <div className="mt-2">
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${strength.color}`}
            style={{ width: `${strength.percentage}%` }}
          />
        </div>
      </div>

      {(strength.warning || strength.suggestions.length > 0) && (
        <div className="mt-2 space-y-1">
          {strength.warning && (
            <p className="text-sm text-red-600">{strength.warning}</p>
          )}
          {strength.suggestions.map((suggestion, index) => (
            <p key={index} className="text-sm text-gray-500">{suggestion}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function getIconColor(strength: PasswordStrength): string {
  switch (strength.score) {
    case 0:
      return 'text-red-500';
    case 1:
      return 'text-orange-500';
    case 2:
      return 'text-yellow-500';
    case 3:
      return 'text-green-500';
    case 4:
      return 'text-green-600';
    default:
      return 'text-gray-400';
  }
}